/**
 * Water Changes Drawer Component
 * Drawer for configuring water change standards
 * Enhanced with spring physics animation
 */

import React, { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export const WaterChangesDrawer = ({ isOpen, onClose, onSave, initialLevel = 'standard' }) => {
  const [selectedLevel, setSelectedLevel] = useState(initialLevel);

  // Update selected level when initialLevel changes
  useEffect(() => {
    setSelectedLevel(initialLevel);
  }, [initialLevel]);

  const handleSave = () => {
    onSave?.(selectedLevel);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with fade animation */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Drawer with spring physics */}
          <motion.div
            className="fixed top-0 right-0 h-full w-full max-w-[640px] bg-white z-50 shadow-2xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200,
              mass: 0.8,
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">
                <h2 className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black">
                  Set Water Changes Standards
                </h2>
                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  type="button"
                >
                  <X className="size-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-8 py-6">
                {/* Options */}
                <div className="space-y-4 mb-8">
                  {/* Frequent */}
                  <label
                    className={`
                      flex items-start p-5 border-2 rounded-lg cursor-pointer transition-all
                      ${
                        selectedLevel === 'frequent'
                          ? 'border-[#61b8e7] bg-[#61b8e7]/5'
                          : 'border-gray-300 hover:border-gray-400'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="waterChangeLevel"
                      value="frequent"
                      checked={selectedLevel === 'frequent'}
                      onChange={() => setSelectedLevel('frequent')}
                      className="mt-1 mr-4 w-5 h-5 text-[#61b8e7] focus:ring-[#61b8e7]"
                    />
                    <div className="flex-1">
                      <div className="font-['Roboto:Bold',sans-serif] font-bold text-[16px] text-black mb-1">
                        Frequent
                      </div>
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600 leading-relaxed">
                        Change water when it is 20% less dirty than the standard.
                      </p>
                    </div>
                  </label>

                  {/* Standard */}
                  <label
                    className={`
                      flex items-start p-5 border-2 rounded-lg cursor-pointer transition-all
                      ${
                        selectedLevel === 'standard'
                          ? 'border-[#61b8e7] bg-[#61b8e7]/5'
                          : 'border-gray-300 hover:border-gray-400'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="waterChangeLevel"
                      value="standard"
                      checked={selectedLevel === 'standard'}
                      onChange={() => setSelectedLevel('standard')}
                      className="mt-1 mr-4 w-5 h-5 text-[#61b8e7] focus:ring-[#61b8e7]"
                    />
                    <div className="flex-1">
                      <div className="font-['Roboto:Bold',sans-serif] font-bold text-[16px] text-black mb-1">
                        Standard
                      </div>
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600 leading-relaxed">
                        Change water bottom of bucket is no longer visible{' '}
                        <span className="font-['Roboto:Bold',sans-serif] font-bold">
                          (100% baseline)
                        </span>
                        .
                      </p>
                    </div>
                  </label>

                  {/* Delayed */}
                  <label
                    className={`
                      flex items-start p-5 border-2 rounded-lg cursor-pointer transition-all
                      ${
                        selectedLevel === 'delayed'
                          ? 'border-[#61b8e7] bg-[#61b8e7]/5'
                          : 'border-gray-300 hover:border-gray-400'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="waterChangeLevel"
                      value="delayed"
                      checked={selectedLevel === 'delayed'}
                      onChange={() => setSelectedLevel('delayed')}
                      className="mt-1 mr-4 w-5 h-5 text-[#61b8e7] focus:ring-[#61b8e7]"
                    />
                    <div className="flex-1">
                      <div className="font-['Roboto:Bold',sans-serif] font-bold text-[16px] text-black mb-1">
                        Delayed
                      </div>
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600 leading-relaxed">
                        Change water when it is 20% more dirty than a standard change.
                      </p>
                    </div>
                  </label>
                </div>

                {/* Note */}
                <div className="bg-gray-100 rounded-lg p-5 border border-gray-200">
                  <div className="flex items-start gap-3">
                    <div className="bg-gray-300 rounded-full p-1 shrink-0 mt-0.5">
                      <Info className="size-4 text-gray-700" />
                    </div>
                    <div>
                      <h3 className="font-['Roboto:Bold',sans-serif] font-bold text-[16px] text-black mb-2">
                        Note
                      </h3>
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-700 leading-relaxed italic">
                        Water change frequency depends on the soil load in each area. Heavily soiled
                        areas need water changes more often, while lightly soiled areas can go
                        longer between changes. You can tweak these settings in the Soil Load
                        Categories tab to optimize your schedule.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-8 py-6 border-t border-gray-200">
                <button
                  onClick={handleSave}
                  className="w-full bg-[#61b8e7] hover:bg-[#4da3d1] text-white font-['Roboto:Bold',sans-serif] font-bold text-[16px] py-3.5 rounded-lg transition-colors"
                  type="button"
                >
                  Save and Continue
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
